import {
  useGlobalComponentForAttribute,
  useOverrides,
  useModelAndAttributeFromPath,
  useModelContext
} from '@rhino-project/core/hooks';
import { RhinoResource } from '@rhino-project/core';

import {
  ModelDisplayAttachment,
  ModelDisplayAttachmentImage,
  ModelDisplayAttachments,
  ModelDisplayArray,
  ModelDisplayArrayReference,
  ModelDisplayBoolean,
  ModelDisplayCurrency,
  ModelDisplayDate,
  ModelDisplayDateTime,
  ModelDisplayEnum,
  ModelDisplayFloat,
  ModelDisplayInteger,
  ModelDisplayReference,
  ModelDisplayString,
  ModelDisplayText,
  ModelDisplayTime,
  ModelDisplayYear
} from '../../ModelDisplay';

const defaultComponents = {
  ModelDisplayArray,
  ModelDisplayArrayReference,
  ModelDisplayAttachment,
  ModelDisplayAttachmentImage,
  ModelDisplayAttachments,
  ModelDisplayBoolean,
  ModelDisplayCurrency,
  ModelDisplayDate,
  ModelDisplayDateTime,
  ModelDisplayEnum,
  ModelDisplayFloat,
  ModelDisplayInteger,
  ModelDisplayReference,
  ModelDisplayString,
  ModelDisplayText,
  ModelDisplayTime
};

export interface ModelDisplayGroupProps {
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  [key: string]: unknown;
}

export const ModelDisplayGroupBase = ({
  overrides,
  ...props
}: ModelDisplayGroupProps) => {
  const {
    ModelDisplayArray,
    ModelDisplayArrayReference,
    ModelDisplayAttachment,
    ModelDisplayAttachmentImage,
    ModelDisplayAttachments,
    ModelDisplayBoolean,
    ModelDisplayCurrency,
    ModelDisplayDate,
    ModelDisplayDateTime,
    ModelDisplayEnum,
    ModelDisplayFloat,
    ModelDisplayInteger,
    ModelDisplayReference,
    ModelDisplayString,
    ModelDisplayText,
    ModelDisplayTime
    // eslint-disable-next-line @eslint-react/static-components
  } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );
  const { model } = useModelContext();
  const { path } = props;
  const { attribute } = useModelAndAttributeFromPath(
    model as RhinoResource,
    path
  );

  // FIXME: Make this a separate function so that its easier to override
  switch (attribute?.type) {
    case 'array': {
      // items is a heterogeneous OpenAPI schema (SchemaObject | ReferenceObject);
      // the dispatch inspects its dynamic type/anyOf shape.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items = attribute.items as any;
      switch (items?.type) {
        case 'string':
        case 'integer':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayArray {...props} />;
        default:
          if (
            items?.anyOf?.[0]?.['$ref'] ===
            '#/components/schemas/active_storage_attachment'
          ) {
            // eslint-disable-next-line @eslint-react/static-components
            return <ModelDisplayAttachments {...props} />;
          }
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayArrayReference {...props} />;
      }
    }
    case 'boolean':
      // eslint-disable-next-line @eslint-react/static-components
      return <ModelDisplayBoolean {...props} />;
    case 'integer':
      switch (attribute.format) {
        case 'currency':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayCurrency {...props} />;
        case 'year':
          return <ModelDisplayYear {...props} />;
        default:
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayInteger {...props} />;
      }
    case 'decimal':
    case 'number':
      if (attribute.format === 'currency')
        // eslint-disable-next-line @eslint-react/static-components
        return <ModelDisplayCurrency {...props} />;

      // eslint-disable-next-line @eslint-react/static-components
      return <ModelDisplayFloat {...props} />;

    case 'reference':
      if (attribute.name.endsWith('_attachment')) {
        if (attribute.format === 'image') {
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayAttachmentImage {...props} />;
        } else {
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayAttachment {...props} />;
        }
      }
      // eslint-disable-next-line @eslint-react/static-components
      return <ModelDisplayReference {...props} />;
    case 'string':
      // eslint-disable-next-line @eslint-react/static-components
      if (attribute?.enum) return <ModelDisplayEnum {...props} />;

      switch (attribute.format) {
        case 'date':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayDate {...props} />;
        case 'time':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayTime {...props} />;
        case 'datetime':
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayDateTime {...props} />;
        default:
          // eslint-disable-next-line @eslint-react/static-components
          return <ModelDisplayString {...props} />;
      }
    case 'text':
      // eslint-disable-next-line @eslint-react/static-components
      return <ModelDisplayText {...props} />;
    default:
      console.assert(false, 'No available display for ', attribute);
  }

  return 'No Display for this attribute type';
};

export const ModelDisplayGroup = (props: ModelDisplayGroupProps) =>
  useGlobalComponentForAttribute(
    'ModelDisplayGroup',
    ModelDisplayGroupBase,
    props
  );
